import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { useAdminApi } from '../hooks/useAdminApi';
import DataTable from '../components/DataTable';
import ConfirmDialog from '../components/ConfirmDialog';
import EntityForm from '../components/EntityForm';
import JsonImportExport from '../components/JsonImportExport';

const columns = [
  { key: 'mod_label', label: 'Label', type: 'text', sortable: true },
  { key: 'mod_name', label: 'Code Name', type: 'text', sortable: true },
  { key: 'rarity', label: 'Rarity', type: 'badge', sortable: true },
  { key: 'purchase_currency', label: 'Currency', type: 'text', sortable: true },
  { key: 'purchase_cost', label: 'Cost', type: 'number', sortable: true },
];

const formFields = [
  { key: 'mod_label', label: 'Display Name', type: 'text', required: true },
  { key: 'mod_name', label: 'Code Name', type: 'text', required: true, placeholder: 'e.g. perimeter_ml' },
  { key: 'rarity', label: 'Rarity', type: 'select', required: true, options: ['epic', 'legendary'] },
  { key: 'purchase_currency', label: 'Currency', type: 'select', required: true, options: ['basic_mod_parts', 'elite_mod_parts'] },
  { key: 'purchase_cost', label: 'Cost', type: 'number', required: true, min: 0 },
];

const tableFilters = [
  { key: 'rarity', label: 'Rarity', options: ['epic', 'legendary'] },
];

const AdminModsPage = () => {
  const { resolvedTheme } = useTheme();
  const dark = resolvedTheme === 'dark';
  const { post } = useAdminApi();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editItem, setEditItem] = useState(null);
  const [editIndex, setEditIndex] = useState(-1);
  const [showForm, setShowForm] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [showImportExport, setShowImportExport] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const res = await fetch('/mod_list.json');
      setData(await res.json());
    } catch { /* empty */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleSave = async (formData) => {
    try {
      if (editIndex >= 0) {
        await post('/admin-update-data.php', { file: 'mod_list.json', action: 'edit', data: formData, item_index: editIndex });
      } else {
        await post('/admin-update-data.php', { file: 'mod_list.json', action: 'add', data: formData });
      }
      setShowForm(false);
      await loadData();
    } catch (err) { alert('Save failed: ' + err.message); }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await post('/admin-update-data.php', { file: 'mod_list.json', action: 'delete', item_index: deleteTarget.index });
      setDeleteTarget(null);
      await loadData();
    } catch (err) { alert('Delete failed: ' + err.message); }
  };

  const handleImport = async (imported) => {
    try {
      await post('/admin-update-data.php', { file: 'mod_list.json', action: 'update', data: imported });
      setShowImportExport(false);
      await loadData();
    } catch (err) { alert('Import failed: ' + err.message); }
  };

  if (loading) return <div className={dark ? 'text-gray-400' : 'text-gray-500'}>Loading...</div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className={`text-xl font-bold ${dark ? 'text-white' : 'text-gray-900'}`}>Mods</h1>
        <div className="flex gap-2">
          <button onClick={() => setShowImportExport(true)}
            className={`px-3 py-2 rounded-lg text-sm font-medium border ${dark ? 'border-[#333] text-gray-300 hover:bg-[#333]' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}>
            Import/Export
          </button>
          <button onClick={() => { setEditItem(null); setEditIndex(-1); setShowForm(true); }}
            className={`px-3 py-2 rounded-lg text-sm font-medium text-white ${dark ? 'bg-[#FF9800] hover:bg-[#F57C00]' : 'bg-indigo-600 hover:bg-indigo-700'}`}>
            + Add New
          </button>
        </div>
      </div>

      <DataTable
        columns={columns}
        data={data}
        filters={tableFilters}
        onEdit={(row, index) => { setEditItem(row); setEditIndex(index); setShowForm(true); }}
        onDelete={(row, index) => setDeleteTarget({ row, index })}
        onDuplicate={(row) => { setEditItem({ ...row, mod_label: row.mod_label + ' (Copy)', mod_name: row.mod_name + '_copy' }); setEditIndex(-1); setShowForm(true); }}
      />

      <EntityForm open={showForm} title={editIndex >= 0 ? 'Edit Mod' : 'Add Mod'} fields={formFields} initialData={editItem} onSave={handleSave} onCancel={() => setShowForm(false)} />
      <ConfirmDialog open={!!deleteTarget} title="Delete Mod" message={`Delete "${deleteTarget?.row?.mod_label}"?`} onConfirm={handleDelete} onCancel={() => setDeleteTarget(null)} />
      <JsonImportExport open={showImportExport} title="Mods" data={data} onImport={handleImport} onClose={() => setShowImportExport(false)} />
    </div>
  );
};

export default AdminModsPage;
